import React from 'react';
import { useSelector } from 'react-redux';
import { makeStyles } from 'tss-react/mui';
import { getDeepaiTheme } from '../../../deepai-theme/functions';
import { formatLeft, getSessionTimeoutLeftMs } from '../../functions';
const useStyles = makeStyles()(() => {
    return {
        root: {
            display: 'inline-flex',
            alignItems: 'center',
            gap: 7,
            padding: '7px 12px',
            lineHeight: 1,
            fontFamily: 'var(--font, system-ui, sans-serif)',
            fontSize: 13,
            fontWeight: 700,
            // 桁が変わっても幅が動かない。1秒ごとに横揺れするのは目障り。
            fontVariantNumeric: 'tabular-nums',
            color: 'var(--text, #eaf0ff)',
            background: 'var(--surface, rgba(20,28,54,.55))',
            border: '1px solid var(--border, rgba(140,165,230,.20))',
            borderRadius: 'var(--radius-sm, 12px)',
            boxShadow: 'var(--shadow, 0 30px 80px -30px rgba(0,0,0,.7))',
            backdropFilter: 'blur(10px)',
            // ツールバーと違って自動で隠さない。カウントダウンが消えたら意味がない。
            pointerEvents: 'none'
        },
        /**
         * バーが出ていない時（テーマ「なし」）の置き場所。
         * 入れる先が無いので自分で浮く。
         */
        floating: {
            position: 'fixed',
            top: 12,
            right: 24,
            zIndex: 401
        },
        near: {
            color: 'var(--danger, #D83848)',
            borderColor: 'var(--danger, #D83848)'
        },
        label: {
            color: 'var(--text-dim, #93a0c4)',
            fontWeight: 500
        }
    };
});
/**
 * 残り時間の時計。
 *
 * 置き場所は2通り。
 *   inBar  … 上部バー(DeepaiTopBar)の中。テーマの帯に並べる
 *   それ以外 … バーが出ていない時（テーマ「なし」）に自分で右上へ浮く
 *
 * バーは `!theme` で描画そのものを返さないので、そこに入れっぱなしにすると
 * テーマを外した人には時計が消える。両方の置き場所を持たせているのはそのため。
 *
 * 出すかどうかは config.js の deepaiSessionTimeout.showClock。
 * clockFromMinutes を入れると、残りがその分数を切ってから出る（0 で常時）。
 *
 * @param {Object} props - inBar: バーの中に置くとき true。
 * @returns {JSX.Element|null}
 */
export default function SessionTimeoutClock({ inBar = false }) {
    const { classes, cx } = useStyles();
    const leftMs = useSelector(getSessionTimeoutLeftMs);
    const opts = useSelector((state) => state['features/base/config'].deepaiSessionTimeout);
    // バーが出る条件は DeepaiTopBar と同じ（テーマが選ばれていること）。
    const hasBar = Boolean(useSelector(getDeepaiTheme));
    const warnSeconds = Number(opts?.warnSeconds) > 0 ? Number(opts?.warnSeconds) : 60;
    if (leftMs === null || opts?.showClock === false) {
        return null;
    }
    // バーがある時はバーの中の1つだけを出す。両方描くと2個並ぶ。
    if (inBar !== hasBar) {
        return null;
    }
    const fromMin = Number(opts?.clockFromMinutes);
    if (Number.isFinite(fromMin) && fromMin > 0 && leftMs > fromMin * 60000) {
        return null;
    }
    return (React.createElement("div", { className: cx(classes.root, !inBar && classes.floating, leftMs <= warnSeconds * 1000 && classes.near) },
        React.createElement("span", { className: classes.label }, '退出まで'),
        React.createElement("span", null, formatLeft(leftMs))));
}
